'use client';
import { Button } from '@shared/components/shadcn';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { UnsubscribePageContainer, UnsubscribeScreenError, UnsubscribeScreenLoading, UnsubscribeScreenPending, UnsubscribeScreenSuccess, useUnsubscribe } from '..';
import { Link } from '../../Link/Link';
import { ToggleGroup, type ToggleOption } from '../../form-controls/ToggleGroup/ToggleGroup';

type UnsubscribeEmailProps = {
  options: ToggleOption[];
};

const buildVerticalMap = (selected: string[], keys: string[]) =>
  keys.reduce(
    (acc, key) => {
      acc[key] = selected.includes(key);
      return acc;
    },
    { bingo: false } as Record<string, boolean>
  );

/**
 * Component to display the unsubscribe email page
 * It handles current preferences and updates them when the user changes the selection.
 * @param options - The options to display in the toggle group
 * @returns The unsubscribe email page
 */
function UnsubscribeEmail({ options }: UnsubscribeEmailProps) {
  const t = useTranslations('unsubscribe');
  const [preferences, setPreferences] = useState<{ userHasChanged: boolean; verticals: string[] | null }>({ userHasChanged: false, verticals: null });
  const { data, isLoading, error, updatePreferences, mutationError, isPending, isSuccess, reset } = useUnsubscribe();
  const verticalKeys = options.map(option => option.value);

  useEffect(() => {
    if (!data || preferences.verticals !== null) return;
    const activeVerticals = verticalKeys.filter(key => data.verticals[key]);
    // Calling setState synchronously within an effect body causes cascading renders that can hurt performance, and is not recommended. (https://react.dev/learn/you-might-not-need-an-effect).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreferences(prev => ({
      ...prev,
      verticals: activeVerticals,
    }));
  }, [data, preferences.verticals, verticalKeys]);

  const handleUnsubscribeAll = () => {
    if (!data) return;
    const { notificationChannels, verticals } = data;
    const updatedNotificationChannels = { ...notificationChannels, email: false };
    const hasOtherChannelActive = Object.values(updatedNotificationChannels).some(Boolean);
    const payload = {
      notificationChannels: updatedNotificationChannels,
      verticals: { ...(hasOtherChannelActive ? verticals : buildVerticalMap([], verticalKeys)), bingo: false },
    };
    updatePreferences(payload);
  };

  const handleSavePreferences = () => {
    if (!data) return;
    const selected = preferences.verticals ?? [];
    if (selected.length === 0) {
      handleUnsubscribeAll();
      return;
    }
    updatePreferences({
      notificationChannels: data.notificationChannels,
      verticals: buildVerticalMap(selected, verticalKeys),
    });
  };

  if (isLoading) return <UnsubscribeScreenLoading />;
  if (error || mutationError) return <UnsubscribeScreenError isFetchingError={!!error} reset={reset} />;
  if (isPending) return <UnsubscribeScreenPending />;
  if (isSuccess) return <UnsubscribeScreenSuccess />;

  return (
    <UnsubscribePageContainer data-track-container="Products">
      <ToggleGroup
        value={preferences.verticals ?? []}
        onValueChange={value => setPreferences({ userHasChanged: true, verticals: value })}
        options={options}
        data-track-container="Options"
        className="gap-4"
      />
      <Button
        variant="primary"
        size="m"
        onClick={preferences.userHasChanged ? handleSavePreferences : handleUnsubscribeAll}
        data-testid="unsubscribe-action-button"
        data-track-container="Actions"
        data-track-name={preferences.userHasChanged ? 'Selected Options' : 'All Options'}
      >
        {preferences.userHasChanged ? t('save') : t('unsubscribe_all')}
      </Button>
      <Link
        href={ROUTES.MY_ACCOUNT.SETTINGS()}
        className="text-primary underline"
        data-testid="manage-preferences-link"
        data-track-container="Actions"
        data-track-name="Manage All"
      >
        {t('manage_preferences')}
      </Link>
    </UnsubscribePageContainer>
  );
}

export { buildVerticalMap, UnsubscribeEmail };
